/**
 * Attachment picker for support-ticket messages. It takes what the API takes — the published
 * support rule (type, size) and how many files a message may carry (SUP-10: it allowed 3 MB where
 * the API takes 10, and any number of files where it takes 5) — and refuses anything else with an
 * inline reason before it is uploaded. Picked files show as removable chips with their size.
 */

import { useEffect, useRef, useState } from "react";

import { acceptFor, formatNames, megabytes, uploadProblem } from "../../lib/uploadRules";
import { useSupportUploadRule } from "../../lib/useUploadRules";

const isPdf = (f: File) => f.type === "application/pdf" || /\.pdf$/i.test(f.name);
const sameFile = (a: File, b: File) => a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function AttachmentPicker({
  files,
  onChange,
  disabled,
}: {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const { rule, perMessage } = useSupportUploadRule();
  const full = files.length >= perMessage;

  // Parent cleared the list (e.g. after a successful send) — drop stale rejection messages.
  useEffect(() => {
    if (files.length === 0) setErrors([]);
  }, [files.length]);

  const add = (picked: File[]) => {
    const rejected: string[] = [];
    const fresh = picked.filter((f) => {
      const problem = uploadProblem(f, rule);
      if (problem) rejected.push(`"${f.name}": ${problem}`);
      return !problem && !files.some((x) => sameFile(x, f));
    });
    const room = Math.max(perMessage - files.length, 0);
    if (fresh.length > room) rejected.push(`A message can carry up to ${perMessage} files.`);
    setErrors(rejected);
    if (room > 0 && fresh.length > 0) onChange([...files, ...fresh.slice(0, room)]);
  };

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || full}
          className="inline-flex items-center gap-1.5 rounded-lg border border-dashed px-3 py-1.5 text-xs font-medium text-brand transition hover:border-brand hover:bg-brand-50 disabled:opacity-50"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
            <path d="M21 11.5l-8.6 8.6a5.5 5.5 0 0 1-7.8-7.8l8.6-8.6a3.7 3.7 0 0 1 5.2 5.2l-8.6 8.6a1.8 1.8 0 0 1-2.6-2.6l7.9-7.9" />
          </svg>
          Attach file
        </button>
        <span className="text-xs text-ink-muted">
          {formatNames(rule)} · max {megabytes(rule.max_bytes)} each · up to {perMessage} files
        </span>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={acceptFor(rule)}
          className="hidden"
          onChange={(e) => {
            add(Array.from(e.target.files ?? []));
            e.target.value = ""; // allow re-picking the same file after removing it
          }}
        />
      </div>

      {files.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {files.map((f, i) => (
            <li key={`${f.name}-${f.size}-${f.lastModified}`} className="flex max-w-[16rem] items-center gap-1.5 rounded-md border bg-white py-1 pl-2 pr-1 text-xs">
              <span
                className={`shrink-0 rounded px-1 text-[10px] font-semibold ${isPdf(f) ? "bg-danger-soft text-danger" : "bg-brand-50 text-brand"}`}
              >
                {isPdf(f) ? "PDF" : "IMG"}
              </span>
              <span className="truncate" title={f.name}>{f.name}</span>
              <span className="shrink-0 text-ink-muted">{formatSize(f.size)}</span>
              <button
                type="button"
                onClick={() => onChange(files.filter((_, j) => j !== i))}
                disabled={disabled}
                aria-label={`Remove ${f.name}`}
                title="Remove"
                // 32px to tap on a touch screen, beside a chip's name (RESP-10: it was 20px).
                className="grid h-6 w-6 shrink-0 place-items-center rounded text-ink-muted hover:bg-danger-soft hover:text-danger pointer-coarse:h-8 pointer-coarse:w-8 disabled:opacity-50"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      {errors.length > 0 && (
        <ul className="space-y-0.5 text-xs text-red-600">
          {errors.map((msg, i) => <li key={i}>{msg}</li>)}
        </ul>
      )}
    </div>
  );
}
